import { APP_NAME, APP_NAME_FULL } from 'config/app';
import { formatEventExhibitionDateRange, momentUTC, todayUTC } from './time';

/**
 * Format the entity type
 *
 * @param  {string} type currently stored as plural from CMS
 *
 * @return {string} type re-formatted as single
 */
export function entityType(type) {
    switch (type) {
        case 'pages':
            return 'page';
        case 'stories':
            return 'story';
        case 'sailings':
            return 'sailing';
        case 'events':
            return 'event';
        case 'exhibitions':
            return 'exhibition';
        case 'educations':
            return 'education';
        default:
            return type;
    }
}

/**
 * Format the search object data
 *
 * @param  {object} item with the attributtes
 *
 * @return {object} item with data
 */
export function searchResult(item) {
    const type = entityType(item.type);

    return {
        ...item,
        type,
        data: item.attributes
    };
}

/**
 * Format page title for event and exhibition types
 *
 * @param  {string} type       Entity type ('event' or 'exhibition')
 * @param  {string} title      Page title
 * @param  {string} startDate  Start date string
 * @param  {string} endDate    End date string
 *
 * @return {string}            Formatted page title
 */
export function formatPageTitle(type, title, startDate, endDate) {
    const plainTitle = title === APP_NAME || title === APP_NAME_FULL ? title : `${title} | ${APP_NAME}`;

    if (type !== 'events' && type !== 'exhibitions') {
        return plainTitle;
    }

    if (isPastEventDate(endDate || startDate)) {
        // Past event/exhibition: "Past Event | Page title"
        return `Past ${type === 'events' ? 'Event' : 'Exhibition'} | ${title} | ${APP_NAME}`;
    }

    // A date range needs both ends to be meaningful.
    if (!startDate || !endDate) {
        return plainTitle;
    }

    // Active event/exhibition: "Page title | Start date - End date"
    const dateRange = formatEventExhibitionDateRange(startDate, endDate);

    return `${title} | ${dateRange} | ${APP_NAME}`;
}

/**
 * Check whether an event/exhibition date has passed
 * - Compares in UTC at start of day, which is more reliable and avoids DST issues
 *
 * @param  {string} date  Date string
 *
 * @return {boolean}      Whether the date is before today
 */
export function isPastEventDate(date) {
    if (!date) return false;

    return momentUTC(date).isBefore(todayUTC());
}

export const JSON_SPACE = 4;

/**
 * Convert JSON object to formatted string
 *
 * @param  {object} jsonObject  JSON object to stringify
 *
 * @return {string}             Formatted JSON string
 */
export function getJsonStr(jsonObject) {
    return JSON.stringify(jsonObject, null, JSON_SPACE);
}
